import { useMemo } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { useSettingsModals } from '@hooks/modals/useSettingsModals';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiUserService } from '@api';
import { ENewNotificationType } from '@enum';
import { Icon } from '@components/icons';
import Typography from '@components/common/typography';
import Info from '@components/common/info';
import { useLocales } from '@hooks/helpers/useLocales';

const Contacts = () => {
    // const { t } = useTranslation('sections', {
    //     keyPrefix: 'settings'
    // });
    const { submitLocale } = useLocales();
    const {
        handleOpenAddEmailModal,
        handleOpenAddPhoneModal,
        handleDeleteEmail,
        handleDeletePhone,
        handleSetPrimaryEmail,
        handleVerifyEmail,
        handleVerifyPhone,
        handleSetPrimaryPhone
    } = useSettingsModals();

    const { data: phones, handleRequest: refetchPhones } = useApiQuery({
        method: () => apiUserService.getPhones(),
        notificationType: ENewNotificationType.PHONE
    });

    const { data: emails, handleRequest: refetchEmails } = useApiQuery({
        method: () => apiUserService.getEmails(),
        notificationType: ENewNotificationType.EMAIL
    });

    const rowsCounter = useMemo(() => {
        return (emails?.length || 0) > (phones?.length || 0)
            ? emails?.length || 0
            : phones?.length || 0;
    }, [emails, phones]);

    const sortedEmails = useMemo(() => {
        return emails?.sort((a) => (a.is_primary ? -1 : 1)) || [];
    }, [emails]);

    const sortedPhones = useMemo(() => {
        return phones?.sort((a) => (a.is_primary ? -1 : 1)) || [];
    }, [phones]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.left}>
                    <div className={styles.icon}>
                        <Icon name="email" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography
                            className={styles.title}
                            variant="body1"
                            lh="120%"
                            weight="semibold">
                            Contact information
                        </Typography>
                        <p className={styles.description}>
                            You can add additional email addresses and phone numbers and set them as
                            primary here
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.content}>
                {Array(rowsCounter)
                    .fill('')
                    .map((_, index) => {
                        const email = sortedEmails?.[index];
                        const phone = sortedPhones?.[index];
                        return (
                            <div key={email?.id || phone?.id} className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    {!!email && (
                                        <>
                                            <div className={styles.label}>
                                                {email?.is_primary ? 'Primary' : 'Additional'} email
                                                address
                                            </div>
                                            <div className={styles.value}>
                                                {email?.email}{' '}
                                                {email?.is_primary ? (
                                                    <Icon name="star_filled" />
                                                ) : (
                                                    <>
                                                        <Info variant="top" content="Delete">
                                                            <button
                                                                onClick={() =>
                                                                    handleDeleteEmail(
                                                                        email,
                                                                        refetchEmails
                                                                    )
                                                                }>
                                                                <Icon name="delete" />
                                                            </button>
                                                        </Info>
                                                        {email?.is_verified ? (
                                                            <Info
                                                                variant="top"
                                                                content="Set as primary">
                                                                <button
                                                                    onClick={() =>
                                                                        handleSetPrimaryEmail(
                                                                            email,
                                                                            refetchEmails
                                                                        )
                                                                    }>
                                                                    <Icon name="star" />
                                                                </button>
                                                            </Info>
                                                        ) : (
                                                            <Info
                                                                variant="top"
                                                                content="Verify email">
                                                                <Button
                                                                    variant="text-gold"
                                                                    onClick={() =>
                                                                        handleVerifyEmail(
                                                                            email,
                                                                            refetchEmails
                                                                        )
                                                                    }>
                                                                    {submitLocale('verify')}
                                                                </Button>
                                                            </Info>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {emails?.length === index + 1 && (
                                                <Button
                                                    variant="text-gold"
                                                    leftIcon="plus"
                                                    size="small"
                                                    className={styles.add_button}
                                                    onClick={() =>
                                                        handleOpenAddEmailModal(refetchEmails)
                                                    }>
                                                    {submitLocale('add_email_address')}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className={styles.data_wrapper}>
                                    {!!phone && (
                                        <>
                                            <div className={styles.label}>
                                                {email?.is_primary ? 'Primary' : 'Additional'} phone
                                                number
                                            </div>
                                            <div className={styles.value}>
                                                {phone?.phone_code?.includes('+')
                                                    ? phone?.phone_code
                                                    : `+${phone?.phone_code}`}{' '}
                                                {phone?.phone_number}
                                                {phone?.is_primary ? (
                                                    <Icon name="star_filled" />
                                                ) : (
                                                    <>
                                                        <Info variant="top" content="Delete">
                                                            <button
                                                                onClick={() =>
                                                                    handleDeletePhone(
                                                                        phone,
                                                                        refetchPhones
                                                                    )
                                                                }>
                                                                <Icon name="delete" />
                                                            </button>
                                                        </Info>
                                                        {phone?.is_verified ? (
                                                            <Info
                                                                variant="top"
                                                                content="Set as primary">
                                                                <button
                                                                    onClick={() =>
                                                                        handleSetPrimaryPhone(
                                                                            phone,
                                                                            refetchPhones
                                                                        )
                                                                    }>
                                                                    <Icon name="star" />
                                                                </button>
                                                            </Info>
                                                        ) : (
                                                            <Info
                                                                variant="top"
                                                                content="Verify phone">
                                                                <Button
                                                                    variant="text-gold"
                                                                    onClick={() =>
                                                                        handleVerifyPhone(
                                                                            phone,
                                                                            refetchPhones
                                                                        )
                                                                    }>
                                                                    {submitLocale('verify')}
                                                                </Button>
                                                            </Info>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {phones?.length === index + 1 && (
                                                <Button
                                                    variant="text-gold"
                                                    leftIcon="plus"
                                                    size="small"
                                                    className={styles.add_button}
                                                    onClick={() =>
                                                        handleOpenAddPhoneModal(refetchPhones)
                                                    }>
                                                    {submitLocale('add_phone_number')}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default Contacts;
